import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, RoleType, Permission } from '@alvo/types';
import { ROLES } from '@alvo/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (userData: Partial<User> & { role: RoleType }) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Permission helpers
  hasPermission: (permission: keyof Permission) => boolean;
  canView: (section: string) => boolean;
  canViewAgentData: (agentId: string) => boolean;

  // Role checks
  isOwner: () => boolean;
  isSuperAdmin: () => boolean;
  isFinanceAdmin: () => boolean;
  isAdmin: () => boolean;
  isSalesDirector: () => boolean;
  isAgent: () => boolean;
}

const SECTION_PERMISSIONS: Record<string, keyof Permission> = {
  revenue: 'viewRevenue',
  grossCommissions: 'viewGrossCommissions',
  agencyMetrics: 'viewAgencyMetrics',
  dealCosts: 'viewDealCosts',
  payrollData: 'viewPayrollData',
  allContacts: 'viewAllContacts',
  allPolicies: 'viewAllPolicies',
  policyCosts: 'viewPolicyCosts',
  fullAnalytics: 'viewFullAnalytics',
  agentPerformance: 'viewAgentPerformance',
  revenueReports: 'viewRevenueReports',
  manageAgents: 'manageAgents',
  commissionSettings: 'manageCommissionRates',
  companySettings: 'manageCompanySettings',
  teamCommissions: 'viewTeamCommissions',
  ownCommissions: 'viewOwnCommissions'
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (userData) => {
        const role = ROLES[userData.role];
        if (!role) {
          set({ error: 'Invalid role' });
          return;
        }

        const user: User = {
          id: userData.id || crypto.randomUUID(),
          email: userData.email || '',
          name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          role: userData.role,
          roleName: userData.roleName || role.name,
          roleLevel: role.level,
          permissions: role.permissions,
          licensedStates: userData.licensedStates || [],
          teamMembers: userData.teamMembers,
          avatarUrl: userData.avatarUrl,
          phone: userData.phone,
          npn: userData.npn,
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          mustChangePassword: userData.mustChangePassword || false,
          capabilities: userData.capabilities || undefined,
        };

        set({
          user,
          isAuthenticated: true,
          error: null
        });
      },

      logout: () => {
        try { localStorage.removeItem('alvo-token'); } catch {}
        set({
          user: null,
          isAuthenticated: false,
          error: null
        });
      },

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      hasPermission: (permission) => {
        const { user } = get();
        return user?.permissions[permission] === true;
      },

      canView: (section) => {
        const { user, hasPermission } = get();
        if (!user) return false;
        const requiredPermission = SECTION_PERMISSIONS[section];
        return requiredPermission ? hasPermission(requiredPermission) : true;
      },

      canViewAgentData: (agentId) => {
        const { user, hasPermission } = get();
        if (!user) return false;
        if (hasPermission('viewAllCommissions')) return true;
        if (hasPermission('viewTeamCommissions') && user.teamMembers?.includes(agentId)) return true;
        if (hasPermission('viewOwnCommissions') && user.id === agentId) return true;
        return false;
      },

      isOwner: () => get().user?.role === 'OWNER',
      isSuperAdmin: () => get().user?.role === 'SUPER_ADMIN',
      isFinanceAdmin: () => get().user?.role === 'FINANCE_ADMIN',
      isAdmin: () => get().user?.role === 'ADMIN',
      isSalesDirector: () => get().user?.role === 'SALES_DIRECTOR',
      isAgent: () => get().user?.role === 'AGENT'
    }),
    {
      name: 'alvo-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

// Selector hooks for common use cases
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useHasPermission = (permission: keyof Permission) =>
  useAuthStore((state) => state.hasPermission(permission));
